import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { Plus, X, Clock, Calendar, Star, Target, AlertCircle } from 'lucide-react';
import { TaskFormData } from '../types/task';
import { createTask } from '../services/tasks';
import { useNotification } from '../contexts/NotificationContext';
import { userService } from '../services/userService';

const FormContainer = styled(motion.div)`
  background: white;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  margin-bottom: 32px;
  border: 1px solid #f1f5f9;
`;

const FormTitle = styled.h3`
  margin: 0 0 32px 0;
  font-size: 28px;
  font-weight: 800;
  background: linear-gradient(135deg, #F57C00 0%, #E65100 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.5px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 700;
  color: #374151;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Input = styled.input`
  padding: 16px 20px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 16px;
  transition: all 0.2s;
  background: #fafafa;

  &:focus {
    outline: none;
    border-color: #F57C00;
    background: white;
    box-shadow: 0 0 0 4px rgba(245, 124, 0, 0.1);
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

const TextArea = styled.textarea`
  padding: 16px 20px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 16px;
  resize: vertical;
  min-height: 120px;
  transition: all 0.2s;
  background: #fafafa;

  &:focus {
    outline: none;
    border-color: #F57C00;
    background: white;
    box-shadow: 0 0 0 4px rgba(245, 124, 0, 0.1);
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

const Select = styled.select`
  padding: 16px 20px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 16px;
  background: #fafafa;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #F57C00;
    background: white;
    box-shadow: 0 0 0 4px rgba(245, 124, 0, 0.1);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const OptionButton = styled.button<{ selected: boolean; variant?: string }>((props: { selected: boolean; variant?: string }) => `
  padding: 12px 20px;
  border: 2px solid ${props.selected ? '#F57C00' : '#e2e8f0'};
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props.selected ? '#F57C00' : 'white'};
  color: ${props.selected ? 'white' : '#374151'};
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: fit-content;

  &:hover {
    border-color: #F57C00;
    background: ${props.selected ? '#E65100' : '#fff7ed'};
    transform: translateY(-1px);
  }

  ${props.variant === 'priority' ? `
    &:nth-child(1) { border-color: ${props.selected ? '#10b981' : '#d1fae5'}; background: ${props.selected ? '#10b981' : 'white'}; }
    &:nth-child(2) { border-color: ${props.selected ? '#f59e0b' : '#fef3c7'}; background: ${props.selected ? '#f59e0b' : 'white'}; }
    &:nth-child(3) { border-color: ${props.selected ? '#ef4444' : '#fee2e2'}; background: ${props.selected ? '#ef4444' : 'white'}; }
  ` : ''}
  ${props.variant === 'credits' ? `
    background: ${props.selected ? 'linear-gradient(135deg, #F57C00 0%, #E65100 100%)' : 'white'};
    color: ${props.selected ? 'white' : '#374151'};
  ` : ''}
`);

const CustomDurationInput = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const DurationInput = styled.input`
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 14px;
  width: 80px;
  text-align: center;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #F57C00;
    box-shadow: 0 0 0 4px rgba(245, 124, 0, 0.1);
  }
`;

const ActionButtonGroup = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  margin-top: 32px;
  flex-wrap: wrap;
`;

const WarningBanner = styled.div`
  padding: 16px;
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 8px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
  color: #92400e;
`;

const SubmitButton = styled(motion.button)`
  background: linear-gradient(135deg, #F57C00 0%, #E65100 100%);
  color: white;
  border: none;
  padding: 16px 32px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(245, 124, 0, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(245, 124, 0, 0.4);
  }

  &:disabled {
    background: #94a3b8;
    transform: none;
    box-shadow: none;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(motion.button)`
  background: #64748b;
  color: white;
  border: none;
  padding: 16px 32px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  transition: all 0.2s;

  &:hover {
    background: #475569;
    transform: translateY(-2px);
  }
`;

interface TaskFormProps {
  onTaskCreated: () => void;
  onCancel: () => void;
}

const TaskForm: React.FC<TaskFormProps> = ({ onTaskCreated, onCancel }) => {
  const [formData, setFormData] = useState<TaskFormData>({
    title: '',
    description: '',
    dueDate: new Date(),
    priority: 'medium',
    category: '',
    estimatedDuration: 30,
    credits: 100,
  });
  const [loading, setLoading] = useState(false);
  const [customDuration, setCustomDuration] = useState(false);
  const [customCategory, setCustomCategory] = useState(false);
  const [canCreateCreditedTasks, setCanCreateCreditedTasks] = useState(false);
  const [usersIApprove, setUsersIApprove] = useState<{ userId: string; displayName: string; email: string; photoURL?: string }[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>('myself');
  const { showSuccess, showError, showWarning } = useNotification();

  useEffect(() => {
    checkCreditApproval();
    fetchUsersIApprove();
  }, []);

  useEffect(() => {
    if (selectedUserId === 'myself' && !canCreateCreditedTasks) {
      setFormData(prev => ({ ...prev, credits: 0 }));
    }
  }, [selectedUserId, canCreateCreditedTasks]);

  const checkCreditApproval = async () => {
    try {
      const canCreate = await userService.hasAcceptedCreditApprover();
      setCanCreateCreditedTasks(canCreate);
    } catch (error) {
      console.error('Error checking credit approval:', error);
      setCanCreateCreditedTasks(false);
    }
  };

  const fetchUsersIApprove = async () => {
    try {
      const users = await userService.getUsersIApprove();
      setUsersIApprove(users);
    } catch (error) {
      setUsersIApprove([]);
    }
  };

  const handleUserChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedUserId(value);
    setFormData(prev => ({
      ...prev,
      userId: value === 'myself' ? undefined : value
    }));
  };

  const handleInputChange = (field: keyof TaskFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      showError('Please enter a task title');
      return;
    }

    if (!formData.category) {
      showError('Please select a category for the task');
      return;
    }

    if (formData.credits === undefined || formData.credits === null) {
      showError('Please select credits for the task');
      return;
    }

    try {
      setLoading(true);
      
      await createTask(formData);
      
      showSuccess('Task created successfully!');
      onTaskCreated();
      setFormData({
        title: '',
        description: '',
        dueDate: new Date(),
        priority: 'medium',
        category: '',
        estimatedDuration: 30,
        credits: 100,
      });
      setCustomDuration(false);
      setCustomCategory(false);
    } catch (error) {
      console.error('Error creating task:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to create task';
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const priorityOptions = [
    { value: 'low', label: 'Low', icon: '🟢' },
    { value: 'medium', label: 'Medium', icon: '🟡' },
    { value: 'high', label: 'High', icon: '🔴' }
  ];

  const creditOptions = [
    { value: 100, label: '100 Credits' },
    { value: 250, label: '250 Credits' },
    { value: 500, label: '500 Credits' },
    { value: 1000, label: '1000 Credits' }
  ];

  // Update category and duration options
  const categoryOptions = [
    { value: 'School', label: 'School' },
    { value: 'Personal', label: 'Personal' },
    { value: 'ECA', label: 'ECA' },
    { value: 'Others', label: 'Others' },
  ];

  const durationOptions = [
    { value: 15, label: '15 min' },
    { value: 30, label: '30 min' },
    { value: 45, label: '45 min' },
    { value: 60, label: '60 min' },
    { value: 9999, label: 'Others' },
  ];

  const allCreditOptions = [{ value: 0, label: 'No Credit' }, ...creditOptions];

  return (
    <FormContainer
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
    >
      <FormTitle>
        <Target />
        Create New Mission
      </FormTitle>
      
      <form onSubmit={handleSubmit}>
        {!canCreateCreditedTasks && (
          <WarningBanner>
            <AlertCircle size={20} />
            <div>
              <strong>Credit Approval Required</strong>
              <br />
              You need at least one approved credit approver to create tasks with credits. 
              Go to User Management to add credit approvers.
            </div>
          </WarningBanner>
        )}

        {usersIApprove.length > 0 && (
          <FormGroup style={{ marginBottom: 20 }}>
            <Label htmlFor="user-select" style={{ marginBottom: 4, fontWeight: 700, color: '#374151', fontSize: 15, letterSpacing: 0.2 }}>
              For
            </Label>
            {usersIApprove.length < 5 ? (
              <ButtonGroup>
                <OptionButton
                  type="button"
                  selected={selectedUserId === 'myself'}
                  onClick={() => {
                    setSelectedUserId('myself');
                    setFormData(prev => ({ ...prev, userId: undefined }));
                  }}
                >
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#64748b', fontSize: 16, border: '1.5px solid #e2e8f0' }}>
                    M
                  </div>
                  <span>Myself</span>
                </OptionButton>
                {usersIApprove.map(user => (
                  <OptionButton
                    key={user.userId}
                    type="button"
                    selected={selectedUserId === user.userId}
                    onClick={() => {
                      setSelectedUserId(user.userId);
                      setFormData(prev => ({ ...prev, userId: user.userId }));
                    }}
                  >
                    {user.photoURL ? (
                      <img src={user.photoURL} alt={user.displayName} style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', border: '1.5px solid #e2e8f0' }} />
                    ) : (
                      <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: '#64748b', fontSize: 16, border: '1.5px solid #e2e8f0' }}>
                        {user.displayName?.[0] || '?'}
                      </div>
                    )}
                    <span>{user.displayName}</span>
                  </OptionButton>
                ))}
              </ButtonGroup>
            ) : (
              <div style={{ position: 'relative', width: '100%' }}>
                <select
                  id="user-select"
                  value={selectedUserId}
                  onChange={handleUserChange}
                  style={{
                    width: '100%',
                    padding: '14px 16px',
                    borderRadius: '10px',
                    fontSize: '16px',
                    border: '2px solid #e2e8f0',
                    background: '#fafafa',
                    color: '#1e293b',
                    fontWeight: 500,
                    outline: 'none',
                    boxShadow: 'none',
                    marginBottom: 0,
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    transition: 'border-color 0.2s',
                  }}
                  onFocus={e => (e.target.style.borderColor = '#F57C00')}
                  onBlur={e => (e.target.style.borderColor = '#e2e8f0')}
                >
                  <option value="myself">Myself</option>
                  {usersIApprove.map(user => (
                    <option key={user.userId} value={user.userId}>
                      {user.displayName} {user.email ? `(${user.email})` : ''}
                    </option>
                  ))}
                </select>
                {/* Custom dropdown arrow */}
                <span style={{
                  position: 'absolute',
                  right: 16,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                  color: '#64748b',
                  fontSize: 18
                }}>▼</span>
              </div>
            )}
          </FormGroup>
        )}

        <FormGrid>
          <FormGroup>
            <Label htmlFor="title">Task Title *</Label>
            <Input
              id="title"
              type="text"
              value={formData.title}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('title', e.target.value)}
              placeholder="Enter task title"
              required
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="dueDate">
              <Calendar size={16} />
              Due Date
            </Label>
            <Input
              id="dueDate"
              type="date"
              value={formData.dueDate.toISOString().split('T')[0]}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('dueDate', new Date(e.target.value))}
            />
          </FormGroup>

          <FormGroup>
            <Label>
              <Star size={16} />
              Priority
            </Label>
            <ButtonGroup>
              {priorityOptions.map((option) => (
                <OptionButton
                  key={option.value}
                  type="button"
                  selected={formData.priority === option.value}
                  variant="priority"
                  onClick={() => handleInputChange('priority', option.value)}
                >
                  {option.icon} {option.label}
                </OptionButton>
              ))}
            </ButtonGroup>
          </FormGroup>

          <FormGroup>
            <Label>Category</Label>
            <div>
              <ButtonGroup>
                {categoryOptions.map((option) => (
                  <OptionButton
                    key={option.value}
                    type="button"
                    selected={formData.category === option.value}
                    onClick={() => handleInputChange('category', option.value)}
                  >
                    {option.label}
                  </OptionButton>
                ))}
              </ButtonGroup>
            </div>
          </FormGroup>

          <FormGroup>
            <Label>
              <Clock size={16} />
              Duration
            </Label>
            <div>
              <ButtonGroup>
                {durationOptions.map((option) => (
                  <OptionButton
                    key={option.value}
                    type="button"
                    selected={formData.estimatedDuration === option.value}
                    onClick={() => handleInputChange('estimatedDuration', option.value)}
                  >
                    {option.label}
                  </OptionButton>
                ))}
              </ButtonGroup>
            </div>
          </FormGroup>

          <FormGroup>
            <Label>Credits</Label>
            {selectedUserId === 'myself' && !canCreateCreditedTasks ? (
              <ButtonGroup>
                <OptionButton
                  type="button"
                  selected={formData.credits === 0}
                  disabled={true}
                  variant="credits"
                  style={{ opacity: 0.7, cursor: 'not-allowed' }}
                >
                  <Star size={16} />
                  No Credit
                </OptionButton>
              </ButtonGroup>
            ) : (
              <ButtonGroup>
                {allCreditOptions.map(option => (
                  <OptionButton
                    key={option.value}
                    type="button"
                    selected={formData.credits === option.value}
                    onClick={() => handleInputChange('credits', option.value)}
                    disabled={false}
                    variant="credits"
                  >
                    <Star size={16} />
                    {option.label}
                  </OptionButton>
                ))}
              </ButtonGroup>
            )}
          </FormGroup>
        </FormGrid>

        <FormGroup>
          <Label htmlFor="description">Description</Label>
          <TextArea
            id="description"
            value={formData.description}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleInputChange('description', e.target.value)}
            placeholder="Add details about your task..."
          />
        </FormGroup>

        <ActionButtonGroup>
          <CancelButton
            type="button"
            onClick={onCancel}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <X />
            Cancel
          </CancelButton>
          <SubmitButton
            type="submit"
            disabled={
              loading ||
              !formData.title.trim() ||
              !formData.category ||
              formData.credits === undefined ||
              formData.credits === null
            }
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Plus />
            {loading ? 'Creating...' : 'Create Mission'}
          </SubmitButton>
        </ActionButtonGroup>
      </form>
    </FormContainer>
  );
};

export default TaskForm; 